import {
  ResponseTimeOperator,
  type TicketStatusV3Condition as TicketStatusV3ConditionType,
} from "@redotech/redo-flows-common/rules-v3/schemas";
import {
  TimeUnit,
  type ConversationStatusV3,
} from "@redotech/redo-flows-common/rules-v3/types";
import { QueryOperator } from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import {
  RedoSingleSelectDropdownInput,
  RedoSingleSelectDropdownInputButton,
} from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { supportTicketStatusItems } from "../support-ticket-status-items";

const statusTimeOperatorOptions = () => [
  {
    id: ResponseTimeOperator.MORE_THAN,
    type: "text" as const,
    text: t("More than", { context: "comparison operator" }),
    value: ResponseTimeOperator.MORE_THAN,
  },
  {
    id: ResponseTimeOperator.LESS_THAN,
    type: "text" as const,
    text: t("Less than", { context: "comparison operator" }),
    value: ResponseTimeOperator.LESS_THAN,
  },
  {
    id: ResponseTimeOperator.BETWEEN,
    type: "text" as const,
    text: t("Between", { context: "comparison operator for a range" }),
    value: ResponseTimeOperator.BETWEEN,
  },
];

const unitOptions = () => [
  {
    id: TimeUnit.MINUTES,
    type: "text" as const,
    text: t("minutes", { context: "time unit" }),
    value: TimeUnit.MINUTES,
  },
  {
    id: TimeUnit.HOURS,
    type: "text" as const,
    text: t("hours", { context: "time unit" }),
    value: TimeUnit.HOURS,
  },
  {
    id: TimeUnit.DAYS,
    type: "text" as const,
    text: t("days", { context: "time unit" }),
    value: TimeUnit.DAYS,
  },
];

export const TicketStatusV3Condition = memo(function TicketStatusV3Condition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TicketStatusV3ConditionType;
  onChange: (updated: TicketStatusV3ConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const statusesEditable =
    editableFields === undefined || editableFields.has("statuses");

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<ConversationStatusV3>[] = useMemo(
    () =>
      supportTicketStatusItems().filter((item) =>
        (condition.statuses || []).includes(item.value),
      ),
    [condition.statuses],
  );

  const displayItem: RedoListItem<ConversationStatusV3> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: selectedItems
          .flatMap((item) => ("text" in item ? [item.text] : []))
          .join(", "),
        value: "OPEN" as ConversationStatusV3,
      };
    }, [selectedItems]);

  const isHasBeen = condition.queryOperator === QueryOperator.HAS_BEEN;

  const selectedTimeOperator = useMemo(() => {
    if (!isHasBeen) {
      return undefined;
    }
    return statusTimeOperatorOptions().find(
      (opt) => opt.value === condition.statusTimeOperator,
    );
  }, [isHasBeen, condition]);

  const selectedUnit = useMemo(() => {
    if (!isHasBeen) {
      return undefined;
    }
    return unitOptions().find((opt) => opt.value === condition.unit);
  }, [isHasBeen, condition]);

  const timeOperatorEditable =
    editableFields === undefined || editableFields.has("statusTimeOperator");
  const valueEditable =
    editableFields === undefined || editableFields.has("value");
  const minMaxEditable =
    editableFields === undefined ||
    editableFields.has("minValue") ||
    editableFields.has("maxValue");
  const unitEditable =
    editableFields === undefined || editableFields.has("unit");

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Status", { context: "support ticket status" })}
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!statusesEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => statusesEditable && setDropdownOpen(!dropdownOpen)}
        placeholder={t("Select statuses")}
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={supportTicketStatusItems()}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!statusesEditable) {
            return;
          }
          onChange({ ...condition, statuses: items.map((item) => item.value) });
        }}
        size="xs"
      />

      {isHasBeen && (
        <>
          <RedoSingleSelectDropdownInput
            disabled={!timeOperatorEditable}
            options={statusTimeOperatorOptions()}
            optionSelected={({ item }) => {
              if (!timeOperatorEditable) {
                return;
              }
              const base = {
                conditionType: condition.conditionType,
                statuses: condition.statuses,
                queryOperator: QueryOperator.HAS_BEEN as const,
                unit: condition.unit,
              };
              if (item.value === ResponseTimeOperator.BETWEEN) {
                onChange({
                  ...base,
                  statusTimeOperator: item.value,
                  minValue: 0,
                  maxValue: 0,
                });
              } else {
                onChange({ ...base, statusTimeOperator: item.value, value: 0 });
              }
            }}
            placeholder={t("Select operator")}
            selectedItem={selectedTimeOperator}
          />

          {(condition.statusTimeOperator === ResponseTimeOperator.MORE_THAN ||
            condition.statusTimeOperator ===
              ResponseTimeOperator.LESS_THAN) && (
            <Flex align="center" gap="sm">
              <RedoTextInput
                inputTypeHint="number"
                min={0}
                placeholder="0"
                setValue={(val: string) => {
                  if (!valueEditable) {
                    return;
                  }
                  const num = parseFloat(val);
                  onChange({ ...condition, value: isNaN(num) ? 0 : num });
                }}
                size="sm"
                state={valueEditable ? "default" : "readonly"}
                value={condition.value.toString()}
              />
              <RedoSingleSelectDropdownInput
                disabled={!unitEditable}
                options={unitOptions()}
                optionSelected={({ item }) => {
                  if (!unitEditable) {
                    return;
                  }
                  onChange({ ...condition, unit: item.value });
                }}
                selectedItem={selectedUnit}
              />
            </Flex>
          )}

          {condition.statusTimeOperator === ResponseTimeOperator.BETWEEN && (
            <Flex align="center" gap="sm">
              <RedoTextInput
                inputTypeHint="number"
                min={0}
                placeholder="0"
                setValue={(val: string) => {
                  if (!minMaxEditable) {
                    return;
                  }
                  const num = parseFloat(val);
                  onChange({ ...condition, minValue: isNaN(num) ? 0 : num });
                }}
                size="sm"
                state={minMaxEditable ? "default" : "readonly"}
                value={condition.minValue.toString()}
              />
              <Text fontSize="sm" textColor="secondary">
                {t("and", { context: "conjunction between two range values" })}
              </Text>
              <RedoTextInput
                inputTypeHint="number"
                min={0}
                placeholder="0"
                setValue={(val: string) => {
                  if (!minMaxEditable) {
                    return;
                  }
                  const num = parseFloat(val);
                  onChange({ ...condition, maxValue: isNaN(num) ? 0 : num });
                }}
                size="sm"
                state={minMaxEditable ? "default" : "readonly"}
                value={condition.maxValue.toString()}
              />
              <RedoSingleSelectDropdownInput
                disabled={!unitEditable}
                options={unitOptions()}
                optionSelected={({ item }) => {
                  if (!unitEditable) {
                    return;
                  }
                  onChange({ ...condition, unit: item.value });
                }}
                selectedItem={selectedUnit}
              />
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
});
